import { Box, withStyles } from '@material-ui/core';
import { SText } from '@setvi/shared/components';
import { TextColor } from '@setvi/shared/enums';
import {
  GPCalcPercentage,
  GPValueCalc
} from 'pages/sales/modules/orders/utils/gp-calc-precentage';
import { useMemo } from 'react';
import { getMOQ, useQuoteStore } from '../store';
import { QuoteItemDTO, QuoteProductDTO, QuoteSectionDTO } from '../types';

export const extractItems = (items: any[], withMetadata = false): any[] =>
  items.map((item: any): any => {
    if (item.type === 'product') {
      const freeHandExtras = {
        isFreeHand: item.isFreeHand,
        vendorName: item?.vendor?.name || '',
        name: item.name,
        sku: item.sku
      };

      return {
        type: item.type,
        sell: item.sell ? Number(item.sell) : 0,
        cost: item.cost ? Number(item.cost) : 0,
        quantity: item.quantity,
        note: item.note || '',
        rfqItemId: item.rfqItemId || undefined,

        productId: item._id,
        ...(item.unitOfMeasure ? { unitOfMeasure: item.unitOfMeasure } : {}),
        children: item.children
          ? extractItems(item.children, withMetadata)
          : [],
        isEditable: item.isEditable,
        ...(item.isFreeHand && freeHandExtras),
        ...(withMetadata && {
          name: item.name,
          sku: item.sku,
          imageUrl: item.imageUrl
          // ...(item?.images?.length && { images: item.images })
        }),
        ...getMOQ(item)
      };
    }
    if (item.type === 'section') {
      return {
        type: item.type,
        name: item.name,
        note: item.note || '',
        children: extractItems(item.children, withMetadata)
      };
    }
    return [];
  });

export const getAllProducts = (quoteItems: QuoteItemDTO[]) => {
  const allRootProducts = quoteItems.filter(
    item => item.type === 'product'
  ) as QuoteProductDTO[];

  const allItemsWithChildren = quoteItems.filter(
    item => item.type === 'product' && item.children
  ) as QuoteProductDTO[];

  const allSubProducts = allItemsWithChildren.flatMap(item => item.children);

  const allSections = quoteItems.filter(
    item => item.type === 'section'
  ) as QuoteSectionDTO[];

  const allSectionProducts = allSections.flatMap(section => section.children);

  return {
    allRootProducts,
    allSubProducts,
    allSectionProducts,
    allProducts: [...allRootProducts, ...allSubProducts, ...allSectionProducts]
  };
};

export const getInvalidProducts = (quoteItems: QuoteItemDTO[]) => {
  const items = extractItems(quoteItems, true);

  const checkProductValidity = (item: QuoteProductDTO) =>
    item.type === 'product' &&
    (item.cost === null ||
      item.cost === 0 ||
      item.sell === null ||
      item.sell === 0);

  const { allProducts } = getAllProducts(items);

  const invalidProducts = allProducts.filter(
    checkProductValidity
  ) as QuoteProductDTO[];

  return invalidProducts;
};

export const FlexBox = withStyles({
  root: {
    display: 'flex',
    gridGap: '7px',
    alignItems: 'center'
  }
})(Box);

export const QuoteSummary = () => {
  const { quote, getTotalItems, getTotalUnits, getTotalCost, getTotalSell } =
    useQuoteStore();

  const { totalItems, totalUnits, totalSell, totalCost } = useMemo(
    () =>
      quote
        ? {
            totalItems: getTotalItems(),
            totalUnits: getTotalUnits(),
            totalSell: getTotalSell(),
            totalCost: getTotalCost()
          }
        : {
            totalItems: 0,
            totalUnits: 0,
            totalSell: 0,
            totalCost: 0
          },
    [quote, getTotalItems, getTotalUnits, getTotalSell, getTotalCost]
  );

  return (
    <Box
      style={{
        width: '100%',
        backgroundColor: '#ffffff',
        padding: '15px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
      <FlexBox>
        <SText
          fontColor={TextColor.LightGrey}
          weight="bold"
          title="TOTAL ITEMS:"
          size="sm"
        />
        <SText title={totalItems?.toString()} weight="bold" size="lg" />
        <SText
          fontColor={TextColor.LightGrey}
          weight="bold"
          size="sm"
          title={`(Units: ${totalUnits})`}
        />
      </FlexBox>
      <FlexBox>
        <SText
          fontColor={TextColor.LightGrey}
          weight="bold"
          title="TOTAL:"
          size="sm"
        />
        <SText
          fontColor={TextColor.Blue}
          title={totalSell?.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}
          weight="bold"
          size="lg"
        />
      </FlexBox>
      <FlexBox>
        <SText
          fontColor={TextColor.LightGrey}
          weight="bold"
          title="COST:"
          size="sm"
        />
        <SText
          title={totalCost?.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}
          weight="bold"
          size="lg"
        />
      </FlexBox>
      <FlexBox>
        <SText
          fontColor={TextColor.LightGrey}
          weight="bold"
          title="GROSS PROFIT:"
          size="sm"
        />
        <SText
          fontColor={TextColor.Green}
          title={`${GPValueCalc(totalSell, totalCost)?.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })} / ${GPCalcPercentage(totalSell, totalCost).toFixed(2)}%`}
          weight="bold"
          size="lg"
        />
      </FlexBox>
    </Box>
  );
};
